import {
  getCountryFromLocale,
  getLanguageFromLocale,
  getOptionalLocaleFromPathname,
  removeLocaleFromUrl,
} from '@susu/locale-utils'

import { getAffiliateDataFromCookie } from './awin-properties'
import { getCustomerProperties } from './customer-properties/customer-properties'
import { getGoogleAnalyticsData } from './google-analytics-properties'
import { getGoogleAndFacebookAttributesFromCookies } from './google-and-facebook-properties'
import {
  getMarketingClickIds,
  type MarketingClickIds,
} from './marketing-click-ids'
import { getPageType } from './page-type-properties/page-type-properties'

export { getAffiliateDataFromCookie } from './awin-properties'
export type { AnalyticsEnvironment } from './google-analytics-properties'
export {
  getGA4ClientIdFromCookie,
  getGA4SessionDataFromCookie,
  resolveGA4MeasurementIdForEnvironment,
  setGA4MeasurementId,
} from './google-analytics-properties'
export { getGoogleAndFacebookAttributesFromCookies } from './google-and-facebook-properties'
export type {
  MarketingClickIdParam,
  MarketingClickIds,
} from './marketing-click-ids'
export {
  getMarketingClickIds,
  MARKETING_CLICK_ID_COOKIE_FALLBACK,
  MARKETING_CLICK_ID_PARAMS,
  MARKETING_CLICK_IDS_EXPIRY_DAYS,
  STORAGE_KEY_MARKETING_CLICK_IDS,
} from './marketing-click-ids'
export type { PageType } from './page-type-properties'
export * from './page-type-properties'

// Captured into the click-ID cookie but not sent as event properties: the
// tracking plan declares no property for them, so every event carrying one
// is a Protocols violation.
const CLICK_IDS_NOT_IN_PLAN = new Set<string>(['sccid', 'dclid', 'li_fat_id'])

/**
 * Get segment common properties
 *
 * Extracts affiliate and marketing attribution data from cookies and URL params,
 * combining data from multiple sources (AWIN, Google, Facebook, Google Analytics,
 * marketing click IDs) for comprehensive analytics tracking.
 */
export const getSegmentCommonProperties = (): {
  affiliateMarker: string
  awc: string
  country: string
  fb_fbc: string
  fb_fbp: string
  ga_aw_clid: string
  ga_client_id: string
  ga_session_id: string
  ga_session_number: string
  logged_in: string
  language: string
  location_id: string
  page_type: string
  url: string
  user_id: string
} & MarketingClickIds => {
  const marketingClickIds = getMarketingClickIds()
  // awc is surfaced explicitly below (real awin cookie, falling back to the
  // captured URL param) — exclude it here so the spread can't clobber that
  // precedence with the raw captured value.
  const { awc: capturedAwc, ...capturedClickIds } = marketingClickIds
  const marketingClickIdsForPayload = Object.fromEntries(
    Object.entries(capturedClickIds).filter(
      ([param]) => !CLICK_IDS_NOT_IN_PLAN.has(param),
    ),
  ) as MarketingClickIds
  const affiliateData = getAffiliateDataFromCookie(capturedAwc)
  const googleAnalyticsData = getGoogleAnalyticsData()
  const googleFacebookData = getGoogleAndFacebookAttributesFromCookies()
  const url = removeLocaleFromUrl(new URL(window.location.href))
  // Not every surface is locale-prefixed: the Custom Made configurator is
  // served from /configurator, where the throwing getLocaleFromPathname took
  // the whole Avo init down with it — the Segment destination was never
  // assigned and the first page event died on `Segment.logPage`.
  const locale = getOptionalLocaleFromPathname(window.location.pathname)
  const language = locale ? getLanguageFromLocale(locale) : ''
  const pageType = getPageType()
  const country = locale ? getCountryFromLocale(locale).toLowerCase() : ''
  const customerProperties = getCustomerProperties()
  const isLoggedIn = customerProperties.isLoggedIn
  const customerId = customerProperties.customerId

  return {
    affiliateMarker: affiliateData.affiliateMarker,
    awc: affiliateData.awc,
    country: country,
    fb_fbc: googleFacebookData.fbFbc,
    fb_fbp: googleFacebookData.fbFbp,
    ga_aw_clid: googleFacebookData.gaAwClid,
    ga_client_id: googleAnalyticsData.ga_client_id,
    ga_session_id: googleAnalyticsData.ga_session_id,
    ga_session_number: googleAnalyticsData.ga_session_number,
    logged_in: isLoggedIn,
    language,
    location_id: window.location.href,
    page_type: pageType,
    url,
    user_id: customerId,
    ...marketingClickIdsForPayload,
  }
}
